/**
 * 看圖開口說面板 — 口說練習的「看圖開口說」分頁(長期路線圖 P3b)。
 *
 * 每個場景的核心循環(沿用句型跟讀 / 口試練習既有機制):
 *   看圖      → 離線預產靜態 PNG(tools/gen-picture-talk-images.mjs 產),不在
 *               執行期生圖
 *   🎙️ 開口說 → 收音(MediaRecorder)+ 語音辨識(Web Speech)一鍵兩用,看圖
 *               自由說英文
 *   評分      → **場景關鍵字覆蓋率**(不依賴視覺模型):說中幾個 expectedKeywords,
 *               顯示「你說到的 / 還可以說的」字
 *   ▶ 聽示範句 → 即時雲端 TTS(既有 /tts fallback 通道,不預產 mp3)
 *   追蹤      → 每場景最近分數落地 localStorage(picture-talk-store,P3c 鋪路)
 *
 * 視覺 = 學術風格設計系統(--ef-* token)。專業介面鐵則:無 UI 音效 / 無彩光 /
 * 無 mascot。學習/遊戲分離:純學習側;錄音只留本機,不上雲。
 */

import { speak, stopSpeaking } from '../../shared/tts-client';
import { academicButton } from '../ui/components';
import {
  PICTURE_TALK_SCENES,
  pictureTalkImageUrl,
  type PictureTalkScene,
} from './picture-talk-data';
import { recordPictureTalkScore } from './picture-talk-store';
import {
  isRecordingSupported,
  isSpeechCheckSupported,
  normalizeWords,
  recognizeSpeech,
  startRecording,
  wordMatches,
  type RecognitionHandle,
  type RecorderHandle,
} from './speech-check';

// ────────────────────────────────────────────────────────────────────────
// 評分(純函式,好測)
// ────────────────────────────────────────────────────────────────────────

/** 一次看圖開口說的關鍵字覆蓋結果。 */
export interface KeywordScore {
  /** 覆蓋率 0..1 = 關鍵字裡被說到的比例。 */
  coverage: number;
  /** 說到的關鍵字。 */
  matched: string[];
  /** 還沒說到的關鍵字。 */
  missed: string[];
}

/** 覆蓋率對應的等第。 */
export interface KeywordRating {
  label: string;
  tone: 'good' | 'ok' | 'warn';
  /** 0..100 百分數。 */
  percent: number;
}

/**
 * 一個關鍵字算不算「說到」:沿用 speech-check 的寬容比對(編輯距離 ≤1),
 * 再放寬詞尾變化 —— 說 `brushes`/`plays` 也算說到 `brush`/`play`(字首相同、
 * 多出的尾巴 ≤2 字母),自由句不該因動詞加 s 就扣分。
 */
export function keywordMatches(keyword: string, heard: string): boolean {
  if (wordMatches(keyword, heard)) return true;
  return heard.startsWith(keyword) && heard.length - keyword.length <= 2;
}

/**
 * 算場景關鍵字覆蓋:每個 expectedKeyword 只要逐字稿任一字對上就算說到。
 * (關鍵字彼此不同,不需多重集合;一個字可同時對上多個關鍵字也無妨。)
 */
export function scoreKeywordCoverage(expectedKeywords: string[], transcript: string): KeywordScore {
  const heardWords = normalizeWords(transcript);
  const matched: string[] = [];
  const missed: string[] = [];
  for (const kw of expectedKeywords) {
    if (heardWords.some((hw) => keywordMatches(kw, hw))) matched.push(kw);
    else missed.push(kw);
  }
  const total = expectedKeywords.length;
  const coverage = total === 0 ? 0 : matched.length / total;
  return { coverage, matched, missed };
}

/**
 * 由覆蓋率給等第。自由開口說不可能(也不需要)說中全部關鍵字,門檻比跟讀的
 * 逐字評分寬:≥0.6 優秀 / ≥0.4 良好 / ≥0.2 加油 / 否則再試試。
 */
export function rateKeywordCoverage(coverage: number): KeywordRating {
  const percent = Math.round(coverage * 100);
  if (coverage >= 0.6) return { label: '優秀', tone: 'good', percent };
  if (coverage >= 0.4) return { label: '良好', tone: 'good', percent };
  if (coverage >= 0.2) return { label: '加油', tone: 'ok', percent };
  return { label: '再試試', tone: 'warn', percent };
}

// ────────────────────────────────────────────────────────────────────────
// 面板
// ────────────────────────────────────────────────────────────────────────

/** 面板把手:離開分頁 / 畫面時呼叫 destroy 收尾(停音 / 停錄 / 停辨識)。 */
export interface PictureTalkPanelHandle {
  destroy(): void;
}

/**
 * 把看圖開口說面板掛進 `host`(host 先清空)。回傳 destroy 把手。
 */
export function mountPictureTalk(
  host: HTMLElement,
  scenes: PictureTalkScene[] = PICTURE_TALK_SCENES,
): PictureTalkPanelHandle {
  host.innerHTML = '';

  const speechOk = isSpeechCheckSupported();
  const recordOk = isRecordingSupported();

  let sceneIndex = 0;

  // ── 進行中媒體控制把手(換場景 / 離開時收尾)──
  let recorder: RecorderHandle | null = null;
  let recognition: RecognitionHandle | null = null;
  let recTimer: ReturnType<typeof setInterval> | null = null;
  let lastUrl: string | null = null;

  const stopAll = (): void => {
    stopSpeaking();
    if (recTimer) { clearInterval(recTimer); recTimer = null; }
    try { recognition?.stop(); } catch { /* noop */ }
    recognition = null;
    if (recorder) { try { void recorder.stop(); } catch { /* noop */ } recorder = null; }
    if (lastUrl) { try { URL.revokeObjectURL(lastUrl); } catch { /* noop */ } lastUrl = null; }
  };

  /** 示範句:即時雲端 TTS(固定句不多,沿用既有 /tts fallback 通道)。 */
  const playSample = (scene: PictureTalkScene): void => {
    stopSpeaking();
    void speak(scene.sampleSentence, { lang: 'en', voice: 'cloud' });
  };

  // ── 版面骨架 ──
  const intro = document.createElement('p');
  intro.textContent = '看圖,用英文說說看:圖裡有誰?在做什麼?有什麼東西?說得愈完整分數愈高。';
  intro.style.cssText = 'font-size:15px;color:var(--ef-ink-soft);line-height:1.7;margin:0 0 14px;';
  host.appendChild(intro);

  if (!speechOk || !recordOk) {
    const note = document.createElement('div');
    note.className = 'pictalk-env-note';
    note.style.cssText =
      'font-size:13px;color:var(--ef-sub);background:var(--ef-paper-bright);border:1px solid var(--ef-line);' +
      'border-radius:var(--ef-radius);padding:10px 12px;margin-bottom:12px;line-height:1.6;';
    const msgs: string[] = [];
    if (!recordOk) msgs.push('此瀏覽器不支援錄音回放。');
    if (!speechOk) msgs.push('此瀏覽器不支援自動口說評分(建議用電腦版 Chrome / Edge)。');
    note.textContent = '提示:' + msgs.join(' ') + ' 仍可看圖、聽示範句練習。';
    host.appendChild(note);
  }

  const stage = document.createElement('div');
  stage.className = 'pictalk-stage';
  host.appendChild(stage);

  const render = (): void => {
    stage.innerHTML = '';
    if (sceneIndex >= scenes.length) sceneIndex = scenes.length - 1;
    if (sceneIndex < 0) sceneIndex = 0;
    const scene = scenes[sceneIndex];
    if (!scene) return;

    // 進度
    const prog = document.createElement('div');
    prog.className = 'pictalk-progress';
    prog.textContent = `第 ${sceneIndex + 1} / ${scenes.length} 張`;
    prog.style.cssText =
      'font-family:var(--ef-serif);font-weight:700;font-size:16px;color:var(--ef-ink);margin-bottom:10px;';
    stage.appendChild(prog);

    // 場景卡(圖 + 中文提示)
    const card = document.createElement('div');
    card.className = 'pictalk-card';
    card.style.cssText =
      'padding:18px 20px;background:var(--ef-paper-bright);border:1px solid var(--ef-line);' +
      'border-radius:var(--ef-radius);box-shadow:var(--ef-shadow-card);';
    const img = document.createElement('img');
    img.className = 'pictalk-img';
    img.src = pictureTalkImageUrl(scene);
    img.alt = '看圖開口說場景圖';
    img.style.cssText =
      'display:block;width:100%;max-width:512px;aspect-ratio:4/3;object-fit:cover;' +
      'border-radius:var(--ef-radius);border:1px solid var(--ef-line);background:var(--ef-paper);';
    const zh = document.createElement('div');
    zh.className = 'pictalk-zh';
    zh.textContent = '提示:' + scene.zh;
    zh.style.cssText = 'margin-top:10px;font-size:15px;color:var(--ef-ink-soft);line-height:1.6;';
    card.append(img, zh);
    stage.appendChild(card);

    // ── 操作列 ──
    const controls = document.createElement('div');
    controls.style.cssText = 'display:flex;flex-wrap:wrap;gap:10px;margin-top:16px;align-items:center;';
    stage.appendChild(controls);

    const result = document.createElement('div');
    result.className = 'pictalk-result';
    result.style.cssText = 'margin-top:14px;display:flex;flex-direction:column;gap:10px;';

    let recording = false;
    const recBtn = academicButton({
      label: '🎙️ 開口說',
      variant: 'primary',
      onClick: () => {
        if (recording) { void finishRecording(); return; }
        void beginRecording();
      },
    });
    recBtn.classList.add('pictalk-record');
    controls.appendChild(recBtn);

    // 示範句:先說過一次再聽比較有挑戰;按下同時顯示句子文字。
    let sampleShown = false;
    const sampleBtn = academicButton({
      label: '▶ 聽示範句',
      variant: 'secondary',
      onClick: () => {
        playSample(scene);
        if (!sampleShown) {
          sampleShown = true;
          const sampleEl = document.createElement('div');
          sampleEl.className = 'pictalk-sample';
          sampleEl.style.cssText =
            'font-size:15px;color:var(--ef-ink-soft);line-height:1.7;font-style:italic;';
          sampleEl.textContent = `示範句:${scene.sampleSentence}`;
          result.appendChild(sampleEl);
          if (!result.isConnected) stage.appendChild(result);
        }
      },
    });
    sampleBtn.classList.add('pictalk-sample-btn');
    controls.appendChild(sampleBtn);

    const timerEl = document.createElement('span');
    timerEl.className = 'pictalk-timer';
    timerEl.style.cssText = 'font-size:14px;color:var(--ef-sub);min-width:64px;';
    controls.appendChild(timerEl);

    let liveTranscriptEl: HTMLElement | null = null;
    let recognitionPromise: Promise<string> | null = null;

    const beginRecording = async (): Promise<void> => {
      recording = true;
      sampleShown = false;
      recBtn.textContent = '⏹ 停止';
      result.innerHTML = '';
      let secs = 0;
      timerEl.textContent = '0 秒';
      recTimer = setInterval(() => { secs += 1; timerEl.textContent = `${secs} 秒`; }, 1000);

      if (speechOk) {
        liveTranscriptEl = document.createElement('div');
        liveTranscriptEl.style.cssText =
          'font-size:14px;color:var(--ef-ink-soft);min-height:20px;font-style:italic;';
        liveTranscriptEl.textContent = '聽你說…';
        result.appendChild(liveTranscriptEl);
        if (!result.isConnected) stage.appendChild(result);
        const { promise, handle } = recognizeSpeech('en-US', (t) => {
          if (liveTranscriptEl) liveTranscriptEl.textContent = t ? `「${t}」` : '聽你說…';
        });
        recognition = handle;
        recognitionPromise = promise.catch(() => '');
      }

      if (recordOk) {
        try {
          recorder = await startRecording();
        } catch (e) {
          console.warn('[picture-talk] 錄音啟動失敗(只用語音辨識):', e);
          recorder = null;
        }
      }
    };

    const finishRecording = async (): Promise<void> => {
      recording = false;
      recBtn.textContent = '🎙️ 再說一次';
      if (recTimer) { clearInterval(recTimer); recTimer = null; }
      timerEl.textContent = '';

      try { recognition?.stop(); } catch { /* noop */ }
      recognition = null;
      const transcript = recognitionPromise ? await recognitionPromise : '';
      recognitionPromise = null;

      let audioUrl: string | null = null;
      if (recorder) {
        try {
          const { url } = await recorder.stop();
          audioUrl = url;
          if (lastUrl) { try { URL.revokeObjectURL(lastUrl); } catch { /* noop */ } }
          lastUrl = url;
        } catch (e) {
          console.warn('[picture-talk] 錄音停止失敗:', e);
        }
        recorder = null;
      }

      renderResult(transcript, audioUrl);
    };

    const renderResult = (transcript: string, audioUrl: string | null): void => {
      result.innerHTML = '';
      if (!result.isConnected) stage.appendChild(result);

      if (audioUrl) {
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;align-items:center;gap:10px;';
        const label = document.createElement('span');
        label.textContent = '你的錄音:';
        label.style.cssText = 'font-size:14px;color:var(--ef-ink-soft);';
        const audio = document.createElement('audio');
        audio.controls = true;
        audio.src = audioUrl;
        audio.className = 'pictalk-playback';
        row.append(label, audio);
        result.appendChild(row);
      }

      if (speechOk) {
        const score = scoreKeywordCoverage(scene.expectedKeywords, transcript);
        const rating = rateKeywordCoverage(score.coverage);

        // P3c 追蹤鉤子:每場景最近分數落地 localStorage(只存資料,不做 UI)。
        recordPictureTalkScore(scene.id, { percent: rating.percent, missed: score.missed });

        const toneColor =
          rating.tone === 'good' ? 'var(--ef-green)'
            : rating.tone === 'ok' ? 'var(--ef-ink-soft)'
              : 'var(--ef-danger, #b4434a)';
        const badge = document.createElement('div');
        badge.className = 'pictalk-score';
        badge.style.cssText =
          `display:inline-flex;align-items:center;gap:8px;font-family:var(--ef-serif);font-weight:700;` +
          `font-size:18px;color:${toneColor};`;
        badge.textContent =
          `說到 ${score.matched.length} / ${scene.expectedKeywords.length} 個重點 · ${rating.label}`;
        result.appendChild(badge);

        if (transcript.trim().length > 0) {
          const detail = document.createElement('div');
          detail.className = 'pictalk-detail';
          detail.style.cssText = 'font-size:14px;color:var(--ef-ink-soft);line-height:1.8;';
          const heard = document.createElement('div');
          heard.textContent = `辨識到:${transcript}`;
          detail.appendChild(heard);
          if (score.matched.length > 0) {
            const hit = document.createElement('div');
            hit.className = 'pictalk-matched';
            hit.innerHTML =
              `你說到的:<span style="color:var(--ef-green);">${score.matched.join('、')}</span>`;
            detail.appendChild(hit);
          }
          if (score.missed.length > 0) {
            const miss = document.createElement('div');
            miss.className = 'pictalk-missed';
            miss.innerHTML =
              `還可以說的:<span style="color:var(--ef-danger,#b4434a);">${score.missed.join('、')}</span>`;
            detail.appendChild(miss);
          }
          result.appendChild(detail);
        } else {
          const noSpeech = document.createElement('div');
          noSpeech.textContent = '沒聽到聲音,再大聲說一次試試。';
          noSpeech.style.cssText = 'font-size:14px;color:var(--ef-sub);';
          result.appendChild(noSpeech);
        }
      }
    };

    stage.appendChild(result);

    // ── 上下張 ──
    const nav = document.createElement('div');
    nav.style.cssText = 'display:flex;gap:10px;margin-top:18px;';
    const prev = academicButton({
      label: '← 上一張',
      variant: 'secondary',
      onClick: () => { if (sceneIndex > 0) { stopAll(); sceneIndex -= 1; render(); } },
    });
    prev.classList.add('pictalk-prev');
    const next = academicButton({
      label: '下一張 →',
      variant: 'secondary',
      onClick: () => { if (sceneIndex < scenes.length - 1) { stopAll(); sceneIndex += 1; render(); } },
    });
    next.classList.add('pictalk-next');
    (prev as HTMLButtonElement).disabled = sceneIndex === 0;
    (next as HTMLButtonElement).disabled = sceneIndex === scenes.length - 1;
    nav.append(prev, next);
    stage.appendChild(nav);
  };

  render();

  return { destroy: stopAll };
}
